import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Single chokepoint for every orchestrator API call. Pulls the current
 * session's access token from supabase-js, attaches `Authorization:
 * Bearer <jwt>`, and throws a typed `ApiError` on non-2xx so consumers
 * get a stable error shape instead of a raw `Response`.
 *
 * On 401, the session is forcibly cleared. The orchestrator's JWT
 * verifier is the authoritative gate (PR1) — a 401 here means the token
 * was rejected (expired, revoked, wrong signer); making the user sign
 * in again is the only correct response.
 */

/** Base URL is baked at build time via `VITE_API_BASE_URL`. */
const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface ApiFetchOptions {
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
  /** HTTP method. Defaults to `GET`. */
  method?: "GET" | "POST" | "PUT";
  /** JSON body for `POST` / `PUT`. Stringified before send; ignored on `GET`. */
  body?: unknown;
}

function buildUrl(path: string, query?: ApiFetchOptions["query"]): string {
  const url = new URL(path, BASE_URL || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function apiFetch<T>(
  client: SupabaseClient,
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session) throw new ApiError(401, null, "no session");

  const method = options.method ?? "GET";
  const hasBody =
    (method === "POST" || method === "PUT") && options.body !== undefined;
  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      // Setting `Content-Type: application/json` on a bodyless request
      // trips Fastify's default JSON parser (`JSON.parse("")` throws)
      // and yields a misleading 400 before the route handler runs.
      // Surfaced when the orphan-drafts POST + retry-validator POST
      // (both body-less) failed in browser; the integration tests work
      // because they omit the header entirely. Match that here.
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
    },
    signal: options.signal,
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
  });

  if (response.status === 401) {
    await client.auth.signOut();
    throw new ApiError(401, null, "unauthorized");
  }

  // Try to parse JSON regardless of status — error responses also carry
  // a JSON body (`{ error: "..." }`) per the orchestrator's convention.
  const text = await response.text();
  const body = text ? safeParseJson(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, body);
  }
  return body as T;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
